import axios, { AxiosError } from "axios";
import { Route as AppRoute } from "utilities/app-routes";

/**
 * Type guard for errors surfaced by the generated Orval client (which throws
 * AxiosErrors). Centralizes the only runtime `axios` reference outside the
 * mutator transport so feature hooks/utilities don't import axios directly.
 */
export const isApiError = (error: unknown): error is AxiosError => axios.isAxiosError(error);

/**
 * Serializes request parameters to URL query string.
 * Handles arrays, null, and undefined values.
 */
export const paramsSerializer = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();

  const appendValue = (key: string, value: unknown) => {
    // Skip empty values to prevent ?param=null or ?param=undefined in URL
    if (value === undefined || value === null) return;

    // Axios and URLSearchParams don't automatically flatten arrays as Spring Boot expects (?sort=id,asc&sort=name,desc)
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  };

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (typeof value === "object" && !Array.isArray(value)) {
      Object.entries(value as Record<string, unknown>).forEach(([nestedKey, nestedValue]) =>
        appendValue(nestedKey, nestedValue),
      );
      return;
    }

    appendValue(key, value);
  });
  return searchParams.toString();
};

/**
 * Response interceptor for handling 401 unauthorized errors.
 * Redirects to logout page when authentication fails.
 */
export const createUnauthorizedResponseInterceptor = () => {
  return (error: AxiosError) => {
    if (error.response?.status === 401) {
      window.location.replace(AppRoute.logout);
    } else {
      const rejectionError = error instanceof Error ? error : new Error(String(error));
      return Promise.reject(rejectionError);
    }
  };
};
